import React from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorPanel } from '~/components/features/ErrorPanel'
import { useCurrentSurvey } from '~/hooks/useCurrentSurvey'
import { compile } from '~/routes/paths'
import { exists, SurveyOp } from '~/utils'

type CheckExists = 'exists'
type CheckHasMappedBrandGroupsWithBrands =
  | 'hasMappedBrandGroupsWithBrands'
  | [
      'hasMappedBrandGroupsWithBrands',
      {
        numberOfBrands?: number
      },
    ]

type Check = CheckExists | CheckHasMappedBrandGroupsWithBrands

const isCheckExists = (check: Check): check is CheckExists => check === 'exists'
const isCheckHasMappedBrandGroupsWithBrands = (
  check: Check,
): check is CheckHasMappedBrandGroupsWithBrands =>
  check === 'hasMappedBrandGroupsWithBrands' ||
  (typeof check === 'object' && check[0] === 'hasMappedBrandGroupsWithBrands')

type CheckCurrentSurveyOptions = {
  backLinkPath?: string
  checks?: Check[]
}

const defaultBackLinkPath = compile('top', {})
const defaultChecks: Check[] = ['exists', 'hasMappedBrandGroupsWithBrands']

export const checkCurrentSurvey = <Props extends {}>(
  WrappedComponent: React.ComponentType<Props>,
  {
    backLinkPath = defaultBackLinkPath,
    checks = defaultChecks,
  }: CheckCurrentSurveyOptions = {},
) => {
  const CurrentSurveyChecker = (props: Props) => {
    const { t } = useTranslation()
    const currentSurvey = useCurrentSurvey()

    const message = React.useMemo(() => {
      const checkExists = checks.find(isCheckExists)
      const checkHasMappedBrandGroupsWithBrands = checks.find(
        isCheckHasMappedBrandGroupsWithBrands,
      )

      if (exists(checkExists)) {
        if (!currentSurvey.success) {
          return t('survey_not_found_2')
        }
      }

      if (exists(checkHasMappedBrandGroupsWithBrands)) {
        if (!currentSurvey.success) {
          return t('survey_not_found_3')
        }

        const { numberOfBrands = 1 } =
          typeof checkHasMappedBrandGroupsWithBrands === 'string'
            ? {}
            : checkHasMappedBrandGroupsWithBrands[1]
        if (
          !SurveyOp.hasMappedBrandGroupsWithBrands(
            currentSurvey.value,
            numberOfBrands,
          )
        ) {
          return t('cannot_be_used_because_setup_is_not_complete_2')
        }
      }

      return null
    }, [currentSurvey, t])

    if (exists(message)) {
      return <ErrorPanel backLinkPath={backLinkPath} message={message} />
    }

    return <WrappedComponent {...props} />
  }

  return CurrentSurveyChecker
}
